import { useMemo, useState, useEffect } from 'react';
import { useTable } from 'react-table';
import EditableCell from '../EditableCell';
import ImportModal from '../ImportModal';

import {
  AddNewRowBtn,
  DocumentationListWrapper,
  HeaderText,
  TableWrapper,
  RemoveRowBtn,
  ImportBtn,
} from './styles';

const tableHeaders = [
  {
    Header: 'Group',
    accessor: 'docGroup',
  },
  {
    Header: 'Document',
    accessor: 'document',
  },
  {
    Header: 'Title 1',
    accessor: 'title1',
  },
  {
    Header: 'Title 2',
    accessor: 'title2',
  },
  {
    Header: 'Title 3',
    accessor: 'title3',
  },
  {
    Header: 'Sta',
    accessor: 'status',
  },
  {
    Header: 'Rev',
    accessor: 'revision',
  },
  {
    Header: () => null,
    id: 'remove_Row_Btn',
    Cell: ({ row: { index }, deleteRow }) => (
      <RemoveRowBtn
        onClick={() => {
          deleteRow(index);
        }}
      />
    ),
  },
];

const defaultColumn = {
  Cell: EditableCell,
};

const DocumentationListEdit = ({
  headerText,
  idCard,
  showUploadBtn,
  tableData,
  setEditedDataField,
}) => {
  const [skipPageReset, setSkipPageReset] = useState(false);
  const columns = useMemo(() => tableHeaders, []);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const Table = ({
    columns,
    data,
    updateCellData,
    skipPageReset,
    editFieldProps,
    deleteRow,
  }) => {
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
      useTable({
        columns,
        data,
        defaultColumn,
        // use the skipPageReset option to disable page resetting temporarily
        autoResetPage: !skipPageReset,
        // updateCellData isn't part of the API, but
        // anything we put into these options will
        // automatically be available on the instance.
        // That way we can call this function from our
        // cell renderer!
        updateCellData,
        editFieldProps,
        deleteRow,
      });

    return (
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            Object.keys(row.values).forEach((valueKey) => {
              row.values[valueKey] = row.values[valueKey] ?? '';
            });
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // When our cell renderer calls updateCell, we'll use
  // the rowIndex, columnId and new value to update the
  // original data
  const updateCellData = (rowIndex, columnId, value) => {
    // We also turn on the flag to not reset the page
    setSkipPageReset(true);

    let currentData = tableData.map((row, index) => {
      if (index === rowIndex) {
        return {
          ...tableData[rowIndex],
          [columnId]: value,
        };
      }
      return row;
    });
    setEditedDataField('documents', currentData);
  };

  const deleteRow = (rowIndex) => {
    setSkipPageReset(true);
    let currentData = [...tableData];
    currentData.splice(rowIndex, 1);
    setEditedDataField('documents', currentData);
  };

  const addNewRow = () => {
    let currentData = [...tableData];
    currentData.push({
      group: '',
      documentNumber: '',
      title1: '',
      title2: '',
      title3: '',
      rev: '',
    });
    setEditedDataField('documents', currentData);
  };

  const openImportModalHandler = () => {
    setIsImportModalOpen(true);
  };

  const closeImportModalHandler = () => {
    setIsImportModalOpen(false);
  };

  // After data changes, we turn the flag back off
  // so that if data actually changes when we're not
  // editing it, the page is reset
  useEffect(() => {
    setSkipPageReset(false);
  }, [tableData]);

  return (
    <DocumentationListWrapper>
      <HeaderText>{headerText}</HeaderText>
      {showUploadBtn && <ImportBtn title={'Import document list'} type={'rounded'} onClick={openImportModalHandler}/>}
      {isImportModalOpen && (
        <ImportModal
          idCard={idCard}
          title="Upload your CSV file"
          resourceType="document"
          description="Please select a CSV file to upload."
          onClose={closeImportModalHandler}
        />
      )}
      <TableWrapper>
        <Table
          columns={columns}
          data={tableData}
          updateCellData={updateCellData}
          skipPageReset={skipPageReset}
          deleteRow={deleteRow}
        />
      </TableWrapper>
      <AddNewRowBtn title={'add row'} onClick={addNewRow} />
    </DocumentationListWrapper>
  );
};

export default DocumentationListEdit;
